import { useCallback, useEffect, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useLoginOrSignupPageData, useLoginOrSignupPageState } from 'src/app/pages/loginOrSignup/pageContext';
import {
	useCanSubmitForm,
	useLoginOrSignup,
	useLoginOrSignupState,
	useLoginOrSignupTracking,
} from 'src/app/pages/loginOrSignup/shared/hooks';

type OtpStepData = {
	otp: string;
	otpError: string | null;
	email: string;
	showPasswordLink: boolean;
};

type OtpStepActions = {
	setOtp: (value: string) => void;
	submitStep: () => Promise<void>;
	resendCode: () => void;
	gotoPasswordStep: () => void;
};

type Result = ModelHookResult<OtpStepData, OtpStepActions>;

export function useOtpStep(): Result {
	const utilityContext = useUtilityContext();
	const { translate } = useI18n();
	const [{ email, otp, authMethod, formErrors, otpSent }, { setError, clearError, setFormData, gotoStep, setOtpSent }] =
		useLoginOrSignupPageState();
	const {
		data: { experiments },
	} = useLoginOrSignupPageData();
	const { requestOtpEmail: requestOtpEmailApi } = useLoginOrSignup();
	const { gotoNextStep } = useLoginOrSignupState();
	const { trackOtpUsePasswordClick, trackOtpSendRateLimit } = useLoginOrSignupTracking();
	const canSubmit = useCanSubmitForm();

	const { mutate: sendOtp } = useMutation({
		mutationFn: async () => requestOtpEmailApi({ email }, utilityContext),
		onSuccess: (response) => {
			if (response.__typename === 'RequestOtpEmailMutationErrorResponse') {
				if (response.error === 'RATE_LIMITED') {
					trackOtpSendRateLimit();
					setError(translate('corgi.login_or_signup.otp.error.rate_limited'));
				} else {
					setError('Unknown error'); // should not happen, no need to translate
				}
			}
		},
		onError: () => {
			setError(translate('corgi.login_or_signup.error.network'));
		},
	});

	const hasSentRef = useRef(false);
	useEffect(() => {
		if (otpSent || hasSentRef.current) return;
		hasSentRef.current = true;
		setOtpSent(true);
		sendOtp();
	}, [otpSent, setOtpSent, sendOtp]);

	const setOtp = useCallback(
		(value: string) => {
			setFormData({ otp: value.replace(/\D/g, '') });
		},
		[setFormData],
	);

	const submitStep = useCallback(async () => {
		if (!canSubmit) return;
		await gotoNextStep();
	}, [gotoNextStep, canSubmit]);

	const resendCode = useCallback(() => {
		setFormData({ otp: '' });
		clearError();
		sendOtp();
	}, [sendOtp, setFormData, clearError]);

	const gotoPasswordStep = useCallback(() => {
		trackOtpUsePasswordClick();
		if (authMethod === 'PASSWORD_SET') {
			gotoStep('login', { canGoBack: true });
		} else {
			gotoStep('no_password', { canGoBack: true });
		}
	}, [trackOtpUsePasswordClick, authMethod, gotoStep]);

	return {
		data: {
			otp,
			otpError: formErrors?.otp ?? null,
			email,
			showPasswordLink: experiments.corgiOtp.variation !== 'v2' || authMethod !== 'PASSWORD_SET',
		},
		actions: { setOtp, submitStep, resendCode, gotoPasswordStep },
	};
}
